/**
 * Tenant-switch invalidation modal for the offline IndexedDB.
 *
 * Mounted once inside <ConfigProvider> (see RequireAuth.tsx). Reads the
 * authenticated tenant via useConfig() and the master flag via
 * isOfflineChecksEnabled(); when the tenant differs from the value last
 * stored in localStorage, decides between three paths:
 *
 *   1. enable_offline_checks is false — do nothing.
 *   2. No pending sessions on this device — clear silently.
 *   3. One or more pending sessions — open a non-dismissible modal that
 *      lets the controller choose between "Sign out" (preserves the
 *      previous user's data) and "Discard and continue" (which then
 *      requires a second confirmation before clearing).
 *
 * Copy and behaviour are documented in
 * docs/offline-quality-checks.md "Switching to a different user".
 */

import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { logoutUser } from "../actions/Auth/actions";
import useConfig from "../actions/Tenants/config/configHook";
import { isOfflineChecksEnabled } from "./db";
import {
    clearAndRecord,
    getPendingSessionCount,
    needsInvalidation,
} from "./tenantInvalidation";

type ModalStep = "initial" | "confirm-discard";

type State =
    | { kind: "idle" }
    | { kind: "checking"; tenantId: string }
    | { kind: "blocked"; tenantId: string; count: number; step: ModalStep }
    | { kind: "clearing"; tenantId: string }
    | { kind: "done" };

export function OfflineTenantInvalidator(): JSX.Element | null {
    const config = useConfig();
    const tenantId: string | undefined = (config as { tenant_id?: string } | null)?.tenant_id;
    const enabled = isOfflineChecksEnabled(config);

    const [state, setState] = useState<State>({ kind: "idle" });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { instance } = useMsal();

    useEffect(() => {
        if (!enabled || !tenantId) {
            setState({ kind: "idle" });
            return undefined;
        }

        if (!needsInvalidation(tenantId)) {
            setState({ kind: "done" });
            return undefined;
        }

        let cancelled = false;
        setState({ kind: "checking", tenantId });

        (async () => {
            try {
                const count = await getPendingSessionCount();
                if (cancelled) return;
                if (count === 0) {
                    setState({ kind: "clearing", tenantId });
                    await clearAndRecord(tenantId);
                    if (cancelled) return;
                    setState({ kind: "done" });
                    return;
                }
                setState({ kind: "blocked", tenantId, count, step: "initial" });
            } catch (err) {
                // Anything goes wrong reading the DB → do not silently
                // continue. Leave state as `checking` so a future render
                // (after fix) retries; surface to console for debugging.
                // eslint-disable-next-line no-console
                console.error("[offline] tenant-switch pre-clear check failed", err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [enabled, tenantId]);

    if (state.kind !== "blocked") {
        return null;
    }

    const { count, step } = state;
    const goToConfirm = () => setState({ ...state, step: "confirm-discard" });
    const goBackToInitial = () => setState({ ...state, step: "initial" });

    const handleSignOut = () => {
        // Same logout sequence used by the header's logout button (see
        // useSecondaryLinks.tsx) — do not clear stores so the previous
        // user can sign back in and complete sync.
        navigate("/login");
        dispatch(logoutUser());
        instance.logoutRedirect();
    };

    const handleDiscardConfirmed = async () => {
        setState({ kind: "clearing", tenantId: state.tenantId });
        try {
            await clearAndRecord(state.tenantId);
            setState({ kind: "done" });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[offline] discard-and-clear failed", err);
            // Re-show the initial step so the user can try again or sign out.
            setState({ ...state, step: "initial" });
        }
    };

    return (
        <Modal isOpen backdrop="static" keyboard={false} centered>
            {step === "initial" ? (
                <>
                    <ModalHeader>Unsynced quality checks on this device</ModalHeader>
                    <ModalBody>
                        <p>
                            This tablet has <strong>{count} quality check{count === 1 ? "" : "(s)"} saved locally</strong> that
                            have not yet been sent to the server. They were created by the previous user and are still waiting
                            for an internet connection to sync.
                        </p>
                        <p>
                            You&apos;re signing in as a different user (on a different tenant). Continuing will permanently delete
                            the previous user&apos;s locally saved checks. <strong>The data cannot be recovered after this point.</strong>
                        </p>
                        <p>
                            If the previous user is available, please ask them to sign back in and wait for the device to come
                            online so the checks can sync to the server.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleSignOut} autoFocus>
                            Sign out (recommended)
                        </Button>
                        <Button color="danger" outline onClick={goToConfirm}>
                            Discard and continue
                        </Button>
                    </ModalFooter>
                </>
            ) : (
                <>
                    <ModalHeader>
                        Discard {count} unsynced check{count === 1 ? "" : "(s)"}?
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            You&apos;re about to permanently delete <strong>{count} quality check{count === 1 ? "" : "(s)"}</strong> from
                            this device:
                        </p>
                        <ul>
                            <li>This data has not been sent to the server.</li>
                            <li>It cannot be restored after deletion.</li>
                            <li>The previous user will not be able to sign back in and recover it.</li>
                        </ul>
                        <p>Are you sure?</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={goBackToInitial} autoFocus>
                            Back
                        </Button>
                        <Button color="danger" onClick={handleDiscardConfirmed}>
                            Yes, delete {count} check{count === 1 ? "" : "(s)"} and continue
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
}
